import { AlertCircleIcon, CheckIcon } from 'lucide-react-native';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { SurveyOtherTextInput } from './SurveyOtherTextInput';
import { SurveyQuestionLabel } from './SurveyQuestionLabel';
import {
  Checkbox,
  CheckboxGroup,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Text } from '@/components/ui/text';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';
import {
  getMaxSelectedChoices,
  getSurveyOtherText,
  hasOtherItem,
  isOtherSelected,
  SURVEY_OTHER_VALUE,
  type SurveyElement,
} from '../../model/surveySchema';

/**
 * Resets the invalid border the form control cascades onto every option.
 *
 * A rejected group otherwise turns all of its circles red, which reads as each
 * option being wrong rather than one answer being missing. The error message
 * and icon still carry the state, so this removes a misleading cue rather than
 * the only cue.
 */
const INVALID_INDICATOR_RESET = 'data-[invalid=true]:border-outline-400';

interface SurveyCheckboxQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
  /** Error for the Other free-text field, which is a separate form field from the choice. */
  otherErrorMessage?: string;
  hideRequiredMarker?: boolean;
}

/** Renders a multi-choice `checkbox` element as a string-array-valued field. */
export function SurveyCheckboxQuestion({
  element,
  control,
  errorMessage,
  otherErrorMessage,
  hideRequiredMarker,
}: SurveyCheckboxQuestionProps) {
  const choices = element.choices ?? [];

  const showsOther = hasOtherItem(element);

  const maxSelected = getMaxSelectedChoices(element);

  const otherText = getSurveyOtherText(element);

  return (
    <Controller
      name={element.name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const selected = Array.isArray(value) ? value : [];

        /**
         * At the cap, only the boxes already ticked stay usable, matching how
         * SurveyJS's own renderer caps a question. Leaving the ticked ones
         * enabled is what keeps the cap from trapping someone: a restored draft
         * or a lowered cap can put a respondent over it, and they have to be
         * able to tick down before they can tick across.
         */
        const isAtCap = maxSelected !== undefined && selected.length >= maxSelected;

        const isChoiceDisabled = (choice: string) => isAtCap && !selected.includes(choice);

        return (
          <FormControl
            isInvalid={!!errorMessage}
            aria-required={element.isRequired}
            className="gap-2">
            <SurveyQuestionLabel element={element} hideRequiredMarker={hideRequiredMarker} />

            <CheckboxGroup
              value={selected}
              onChange={onChange}
              aria-label={element.isRequired ? `${element.title}, required` : element.title}>
              {choices.map((choice) => (
                <Checkbox
                  testID={`${element.name}-${choice}`}
                  value={choice}
                  key={choice}
                  isDisabled={isChoiceDisabled(choice)}
                  className={TOUCH_TARGET_MIN_H}>
                  <CheckboxIndicator className={INVALID_INDICATOR_RESET}>
                    <CheckboxIcon as={CheckIcon} />
                  </CheckboxIndicator>
                  <CheckboxLabel>{choice}</CheckboxLabel>
                </Checkbox>
              ))}

              {showsOther ? (
                <Checkbox
                  testID={`${element.name}-${SURVEY_OTHER_VALUE}`}
                  value={SURVEY_OTHER_VALUE}
                  key={SURVEY_OTHER_VALUE}
                  isDisabled={isChoiceDisabled(SURVEY_OTHER_VALUE)}
                  aria-label={otherText}
                  className={TOUCH_TARGET_MIN_H}>
                  <CheckboxIndicator className={INVALID_INDICATOR_RESET}>
                    <CheckboxIcon as={CheckIcon} />
                  </CheckboxIndicator>
                  <CheckboxLabel>{otherText}</CheckboxLabel>
                </Checkbox>
              ) : null}
            </CheckboxGroup>

            {maxSelected === undefined ? null : (
              <Text
                className="text-sm text-typography-700"
                accessibilityLiveRegion="polite"
                aria-live="polite">
                {isAtCap
                  ? `${selected.length} of ${maxSelected} selected — clear one to choose a different answer`
                  : `${selected.length} of ${maxSelected} selected`}
              </Text>
            )}

            {showsOther && isOtherSelected(value) ? (
              <SurveyOtherTextInput
                element={element}
                control={control}
                errorMessage={otherErrorMessage}
              />
            ) : null}

            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText>{errorMessage}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
